import React, { useState, useEffect, useRef } from 'react';
import { ProjectProgress, BudgetChart, IssueChart } from '../../components/charts/DashboardCharts';
import { CalenderDays } from "../../modules/default-svg";
import {
  dateOnlyFormatted,
  getAccountCurrency,
  displayDecimalFormatted,
  weeksBetweenDates
} from "../../utils/systemSettings";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { convertNameToEllipsis } from '../../helpers/utils';
import { NoProjects } from './components/NoProjects';
import { ExternalLink } from 'lucide-react';
import useNavigation from '../../utils/useNavigation';
import { getNavigationFromEntityType } from './utils/helper';

const OverviewTable = ({
  overviewDetails,
  dashboardFilterSetName,
  dashboardFilterSetId,
  loading,
  onComponentReady,
  entityType
}) => {
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  const containerRef = useRef(null);
  const [dummyCount, setDummyCount] = useState(4);
  const [columnWidth, setColumnWidth] = useState("15rem");
  const { navigateTo } = useNavigation();
  const programs = overviewDetails || [];

  useEffect(() => {
    if (onComponentReady) {
      const timeout = setTimeout(() => {
        onComponentReady();
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, []);

  const [columnWidths, setColumnWidths] = useState([]);

  useEffect(() => {
    const calculateColumnWidths = () => {
      if (containerRef.current) {
        const pxToRem = (px) => px / 16;
        const remToPx = (rem) => rem * 16;

        const containerWidth = containerRef.current.offsetWidth;
        const labelWidth = 96;
        const availableWidth = containerWidth - labelWidth;
        const defaultColumnWidth = 15;
        const minColumnWidth = 15;

        if (programs.length === 0) {
          setDummyCount(Math.max(1, Math.floor(availableWidth / remToPx(defaultColumnWidth))));
          setColumnWidths([]);
          return;
        }

        const estimatedWidths = programs.map(program => {
          const nameWidth = program?.name ? Math.max(minColumnWidth, pxToRem(program.name.length * 7)) : defaultColumnWidth;
          return nameWidth;
        });

        const totalNeededWidth = estimatedWidths.reduce((sum, width) => sum + remToPx(width), 0);

        if (totalNeededWidth <= availableWidth) {
          setColumnWidths(estimatedWidths.map(width => `${width}rem`));
        } else {
          const extraWidthNeeded = totalNeededWidth - availableWidth;
          const sortedIndices = estimatedWidths
            .map((width, index) => ({ width, index }))
            .sort((a, b) => b.width - a.width);

          const adjustedWidths = [...estimatedWidths];
          let remainingWidthToReduce = pxToRem(extraWidthNeeded);
          let i = 0;

          while (remainingWidthToReduce > 0 && i < sortedIndices.length) {
            const { width, index } = sortedIndices[i];
            const maxReduction = Math.max(0, width - minColumnWidth);
            const reduction = Math.min(maxReduction, remainingWidthToReduce);

            adjustedWidths[index] -= reduction;
            remainingWidthToReduce -= reduction;
            i++;

            if (i === sortedIndices.length && remainingWidthToReduce > 0) {
              adjustedWidths.forEach((_, idx) => {
                adjustedWidths[idx] = minColumnWidth;
              });
              remainingWidthToReduce = 0;
            }
          }

          setColumnWidths(adjustedWidths.map(width => `${Math.max(minColumnWidth, width)}rem`));
        }

        setDummyCount(Math.max(1, Math.floor(availableWidth / remToPx(defaultColumnWidth))));
      }
    };

    calculateColumnWidths();
    window.addEventListener('resize', calculateColumnWidths);
    return () => window.removeEventListener('resize', calculateColumnWidths);
  }, [programs.length]);

  const handleRedirect = (item) => {
    let navigationType = getNavigationFromEntityType(entityType);
    if (navigationType) {
      navigateTo(navigationType, item.id, item.name);
    }
  };

  const categories = [
    {
      key: 'date',
      label: 'Date',
      render: (program) => (
        <div className="flex items-center overview-dates justify-center gap-2">
          <CalenderDays />
          {!program.startDate && !program.endDate ? (
            ''
          ) : (
            <>
              {program.startDate ? dateOnlyFormatted(setting, program.startDate) : ''} <b>to</b>{' '}
              {program.endDate ? dateOnlyFormatted(setting, program.endDate) : ''}
            </>
          )}
        </div>
      )
    },
    {
      key: 'remaining',
      label: 'Duration',
      render: (program) => (
        <div className="flex justify-center overview-remaining-days">
          {(program.startDate && program.endDate)
            ? weeksBetweenDates(program.startDate, program.endDate)
            : '-'}
        </div>
      )
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (program) => (
        <div className="flex justify-center" style={{ zIndex: 0 }}>
          <ProjectProgress progress={program.progress} />
        </div>
      )
    },
    {
      key: 'cost',
      label: 'Budget',
      render: (program) => (
        <div className="flex justify-center">
          <BudgetChart
            budgetSummary={program.costs}
            currencySymbol={getAccountCurrency(setting)}
          />
        </div>
      )
    },
    {
      key: 'issues',
      label: 'Issues',
      render: (program) => (
        <div className="flex justify-center w-full">
          <IssueChart issues={program.issues || []} />
        </div>
      )
    }
  ];

  const renderLoadingSkeleton = () => {
    const dummyPrograms = Array(dummyCount).fill(0);
    const tdStyle = {
      minWidth: "15rem",
      maxWidth: "15rem",
      width: "15rem",
      padding: "1rem 0.5rem"
    };

    return (
      <div className="p-3">
        <table className="w-full border-collapse" style={{ minWidth: "1200px" }}>
          <thead>
            <tr className="border-b">
              <th className="text-left p-3 overview-header-title sticky left-0 bg-white" style={{ minWidth: "6rem", maxWidth: "6rem", width: "6rem" }}>
                <Skeleton width={80} />
              </th>
              {dummyPrograms.map((_, index) => (
                <th key={index} className="text-center p-3 overview-header" style={tdStyle}>
                  <Skeleton width={160} />
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category, index) => (
              <tr key={category.key} className={index < categories.length - 1 ? "border-b" : ""}>
                <td className="text-left p-3 overview-label sticky left-0 bg-white"
                  style={{
                    minWidth: "6rem",
                    maxWidth: "6rem",
                    width: "6rem",
                    zIndex: category.key === 'progress' ? 1 : 0,
                    borderRadius: category.key === 'issues' ? "15px" : 0
                  }}>
                  {category.label}
                </td>
                {dummyPrograms.map((_, programIndex) => (
                  <td key={programIndex} style={tdStyle}>
                    {category.key === 'date' && <Skeleton width={140} />}
                    {category.key === 'remaining' && <Skeleton width={40} />}
                    {category.key === 'progress' && <Skeleton height={20} width={140} style={{ borderRadius: "10px" }} />}
                    {category.key === 'cost' && <Skeleton height={40} width={140} style={{ borderRadius: "10px" }} />}
                    {category.key === 'issues' && <Skeleton height={30} width={140} style={{ borderRadius: "10px" }} />}
                  </td>
                ))}
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="DashboardCard overview-table w-full pt-0" ref={containerRef}>
      {loading ? (
        renderLoadingSkeleton()
      ) : (

        programs && programs.length > 0 ?
          (
            <div className="p-3 pt-0">
              <table className="w-full border-collapse" style={{ minWidth: "1200px" }}>
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 overview-header-title sticky left-0 bg-white" style={{ minWidth: "6rem", maxWidth: "6rem", width: "6rem" }}></th>
                    {programs.map((program, index) => (
                      <th
                        key={program.id}
                        className="text-center p-3 overview-header cursor-pointer"
                        style={{
                          minWidth: columnWidths[index] || "15rem",
                          maxWidth: columnWidths[index] || "15rem",
                          width: columnWidths[index] || "15rem",
                          padding: "1rem 0.5rem"
                        }}
                        onClick={() => {
                          dashboardFilterSetId(program.id);
                          dashboardFilterSetName(program.name);
                        }}
                      >
                        <div className="d-flex flex-col gap-1 align-items-center justify-center">
                          {program.image &&
                            (
                              <div className={`flex flex-col items-center justify-center min-w-10 max-w-30 h-7 overflow-hidden mr-1`}>
                                <img src={program.image} className="max-w-full max-h-full object-contain" />
                              </div>
                            )}
                          <div className='flex gap-1 items-center justify-center'>
                            <p className='text-truncate w-100'>{program.name}</p>
                            <ExternalLink
                              className='overview-header-name-link'
                              size={16}
                              color='#342C82'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRedirect({ id: program.id, name: program.name });
                              }} />
                          </div>
                        </div>
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category, index) => (
                    <tr key={category.key} className={index < categories.length - 1 ? "border-b" : ""}>
                      <td className="text-left p-3 overview-label sticky left-0 bg-white"
                        style={{
                          minWidth: "6rem",
                          maxWidth: "6rem",
                          width: "6rem",
                          zIndex: category.key === 'progress' ? 1 : 0,
                          borderRadius: 0,
                          height: category.key === 'issues' ? "9rem" : 'auto',
                        }}>
                        {category.label}
                      </td>
                      {programs.map((program, index) => (
                        <td
                          key={program.id}
                          style={{
                            minWidth: columnWidths[index] || "15rem",
                            maxWidth: columnWidths[index] || "15rem",
                            width: columnWidths[index] || "15rem",
                            padding: "1rem 0.5rem"
                          }}
                        >
                          {category.render(program)}
                        </td>
                      ))}
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
          :
          NoProjects()

      )}
    </div>
  );
};

export default OverviewTable;